<template>
	<div class="bg-white svg-background">
		<p @click="$router.push({ name: 'Dashboard' })"
		class="fixed top-6 right-7 text-4xl font-bold cursor-pointer">x</p>
		<div class="pt-12 sm:pt-16 lg:pt-28">
			<div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
				<div class="text-center">
					<h2 class="text-3xl font-extrabold text-gray-900 sm:text-4xl lg:text-5xl" style="line-height:1.25;">
						{{ $route.query.title || 'Take your Podcast Player to the next Level!' }}
					</h2>
					<p class="mt-4 mb-20 text-xl leading-relaxed text-gray-700">
						<b class="text-green-600">Upgrade to Podplayer PRO</b> and turn your website into an audio listening experience, which increases your downloads and boosts your audience engagement!
					</p>
				</div>
			</div>
		</div>
		<div class="mt-8 bg-none pb-16 sm:mt-12 sm:pb-20 lg:pb-28">
			<div class="relative">
			<div class="absolute inset-0 h-1/2 bg-none" />
			<div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div class="max-w-lg mx-auto rounded-lg shadow-lg overflow-hidden lg:max-w-none lg:flex">
					<div class="flex-1 bg-white px-6 py-8 lg:p-12">
					<h3 class="text-2xl font-extrabold text-gray-900 sm:text-3xl">
						Podplayer PRO Subscription
					</h3>
					<p class="mt-6 text-base text-gray-500">
						<b>Podplayer PRO</b> is built for podcasters, who want to increase their downloads, get more subscribers and skyrocket their engagment!
					</p>
					<div class="mt-8">
						<div class="flex items-center">
							<h4 class="flex-shrink-0 pr-4 bg-white text-sm tracking-wider font-semibold uppercase text-blue-700">
							What's included
							</h4>
							<div class="flex-1 border-t-2 border-gray-200" />
						</div>
						<ul role="list" class="mt-8 space-y-5 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:gap-y-5">
							<li v-for="feature in includedFeatures" :key="feature" class="flex items-start lg:col-span-1">
							<div class="flex-shrink-0">
								<CheckCircleIcon class="h-5 w-5 text-green-500" aria-hidden="true" />
							</div>
							<p class="ml-3 text-sm text-gray-700">
								<span v-html="feature"></span>
							</p>
							</li>
						</ul>
					</div>
					</div>
					<div class="py-8 px-6 text-center bg-gray-50 lg:flex-shrink-0 lg:flex lg:flex-col lg:justify-center lg:p-12">
					<div v-if="yearlyBilling" class="mt-4 flex items-center text-5xl font-extrabold text-gray-900">
						<div>$9</div>
						<div class="space-y-1">
							<div class="ml-3 text-base text-left font-medium leading-4 text-gray-800">USD / month</div>
							<div class="ml-3 text-sm text-left font-normal leading-4 text-gray-500">Billed yearly ($108)</div>
						</div>
					</div>
					<div v-if="monthlyBilling" class="mt-4 flex items-center text-5xl font-extrabold text-gray-900">
						<div>$12</div>
						<div class="space-y-1">
							<div class="ml-3 text-base text-left font-medium leading-4 text-gray-800">USD / month</div>
							<div class="ml-3 text-sm text-left font-normal leading-4 text-gray-500">Billed every month</div>
						</div>
					</div>
					<div class="my-6"><SwitchWithLabel @click="monthlyBilling = !monthlyBilling"/></div>
					<div class="mt-2">
						<div class="rounded-md shadow">
							<a href=""  @click.prevent="handleBuy" class="flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700">
								Get Access
							</a>
						</div>
					</div>
					</div>
				</div>
			</div>
			</div>
		</div>
	</div>
</template>

<script>
import { computed, ref } from "vue"
import { CheckCircleIcon } from '@heroicons/vue/solid'
import SwitchWithLabel from "../components/SwitchWithLabel.vue"
import StripeService from "../services/StripeService"
import Spinner from "../components/Spinner.vue"

const includedFeatures = [
	'Create <b>unlimited players</b> for 3 different podcast shows',
	'up to <b>100.000  pageviews</b> per month',
	'Remove "Powered by Podplayer.co" Branding',
	'Get <b>priority support</b> ',
	'Access to <b>Latest Player</b>',
	'Access to <b>Archive Player</b> (coming soon)',
]

export default {
	components: { CheckCircleIcon, SwitchWithLabel, Spinner },
	setup() {
		const monthlyBilling = ref(false)
		const yearlyBilling = computed(() => !monthlyBilling.value)	

		const handleBuy = async () => {
			const planID = monthlyBilling.value ? 2 : 3
			const checkoutLink = await StripeService.CreateCheckoutSession(planID)
			window.location.href = checkoutLink
		}

		return {
			includedFeatures, monthlyBilling, yearlyBilling, handleBuy,
		}
	},
}
</script>

<style>
	.svg-background {
		background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' version='1.1' xmlns:xlink='http://www.w3.org/1999/xlink' xmlns:svgjs='http://svgjs.com/svgjs' width='1440' height='560' preserveAspectRatio='none' viewBox='0 0 1440 560'%3e%3cg mask='url(%26quot%3b%23SvgjsMask1065%26quot%3b)' fill='none'%3e%3crect width='1440' height='560' x='0' y='0' fill='rgba(0%2c 0%2c 0%2c 0.04)'%3e%3c/rect%3e%3cpath d='M0%2c421.269C78.044%2c410.589%2c105.561%2c309.683%2c169.101%2c263.126C227.016%2c220.691%2c322.057%2c225.959%2c354.187%2c161.752C386.238%2c97.703%2c328.27%2c24.902%2c324.089%2c-46.597C319.367%2c-127.343%2c363.61%2c-211.628%2c328.101%2c-284.301C290.173%2c-361.924%2c216.532%2c-440.189%2c130.198%2c-443.412C41.383%2c-446.728%2c-5.734%2c-333.376%2c-88.04%2c-299.837C-157.685%2c-271.457%2c-251.918%2c-317.337%2c-306.404%2c-265.5C-360.525%2c-214.01%2c-340.076%2c-124.363%2c-350.264%2c-50.36C-360.382%2c23.135%2c-393.981%2c98.436%2c-365.964%2c167.13C-337.665%2c236.517%2c-263.264%2c271.13%2c-201.714%2c313.873C-137.853%2c358.22%2c-77.031%2c431.811%2c0%2c421.269' fill='rgba(0%2c 0%2c 0%2c 0.04)'%3e%3c/path%3e%3cpath d='M1440 1157.691C1549.992 1159.6970000000001 1621.316 1043.9569999999999 1705.538 973.184 1778.858 911.572 1850.5819999999999 851.9839999999999 1900.824 770.451 1956.595 679.946 2014.71 584.178 2010.711 477.944 2006.54 367.121 1970.03 242.71499999999997 1878.597 179.95299999999997 1787.959 117.738 1665.602 170.78300000000002 1555.785 165.67399999999998 1475.8220000000001 161.954 1400.826 152.591 1320.788 154.00099999999998 1210.164 155.95 1085.896 110.61500000000001 996.3009999999999 175.53199999999998 906.187 240.825 862.163 367.717 872.967 478.473 883.354 584.954 993.8340000000001 647.241 1051.13 737.591 1098.591 812.433 1120.415 898.573 1180.616 963.6089999999999 1255.487 1044.493 1329.8 1155.681 1440 1157.691' fill='rgba(0%2c 0%2c 0%2c 0.04)'%3e%3c/path%3e%3c/g%3e%3cdefs%3e%3cmask id='SvgjsMask1065'%3e%3crect width='1440' height='560' fill='white'%3e%3c/rect%3e%3c/mask%3e%3c/defs%3e%3c/svg%3e");
		background-size: cover;
	}
</style>